/** Pure helpers of the questioning group: labels, grade codes, transcript timeline. */
import type { TFunction } from 'i18next'
import type {
  BookmarkLabel,
  Grade,
  SessionBookmark,
  SpeakerRole,
  TranscriptSegment,
} from '../types'
import {
  TRANSCRIPT_ENTITY_KINDS,
  type TranscriptData,
  type TranscriptEntity,
  type TranscriptEntityKind,
} from './questioning-types'
import { DETECT_LANGUAGE } from './recording-languages'

/** Text of a bookmark label (`humint.common.bookmarkLabels`), or "no label". */
export function bookmarkLabelText(label: BookmarkLabel | null, t: TFunction): string {
  return t(`humint.common.bookmarkLabels.${label ?? 'none'}`)
}

/** Language name of a BCP-47 code, or the upper-cased code when the list has no name for it. */
export function languageName(code: string, t: TFunction): string {
  return t(`humint.common.languages.${code}`, { defaultValue: code.toUpperCase() })
}

/** The names of several languages as one list, in the order given: "Slovak, Czech". */
export const languageNames = (codes: readonly string[], t: TFunction): string =>
  codes.map(code => languageName(code, t)).join(', ')

/**
 * The languages a recording names, as its readout: "Language: Slovak" for one, "Languages:
 * Slovak, Czech" for several in the order chosen, and "Language: Automatic" when none is named
 * and one is detected.
 */
export function languagesReadout(codes: readonly string[], t: TFunction): string {
  if (codes.length > 1) {
    return t('humint.questioning.recording.languages', { languages: languageNames(codes, t) })
  }
  const language = languageName(codes[0] ?? DETECT_LANGUAGE, t)
  return t('humint.questioning.recording.language', { language })
}

/** "C3" for { reliability: 'C', credibility: 3 }. */
export const gradeCode = (grade: Grade): string => `${grade.reliability}${grade.credibility}`

/** Speaker colours: data colours for dots and names only, never body text. */
export const SPEAKER_TONE: Record<SpeakerRole, { dot: string; text: string }> = {
  questioner: { dot: 'bg-cat-1', text: 'text-cat-1' },
  subject: { dot: 'bg-cat-4', text: 'text-cat-4' },
  interpreter: { dot: 'bg-cat-10', text: 'text-cat-10' },
}

export const SPEAKER_ROLES: readonly SpeakerRole[] = ['questioner', 'subject', 'interpreter']

/** How far ahead a position still belongs to the next segment. */
const LEAD_IN_MS = 15_000

/**
 * The segment a playback position belongs to: the one that contains it, else the next one
 * starting within 15 s (a bookmark or citation often sits just before the answer).
 * `segments` are in time order; a binary search on `startMs` keeps it cheap on every tick.
 */
export function segmentAt(segments: readonly TranscriptSegment[], ms: number): string | null {
  // `lo` ends on the first segment starting after `ms`; only the one before it can contain `ms`.
  let lo = 0
  let hi = segments.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (segments[mid].startMs <= ms) lo = mid + 1
    else hi = mid
  }
  const before = lo > 0 ? segments[lo - 1] : undefined
  if (before && ms < before.endMs) return before.id
  const next = segments[lo]
  return next && next.startMs - ms <= LEAD_IN_MS ? next.id : null
}

export type TimelineItem =
  | { type: 'segment'; atMs: number; segment: TranscriptSegment }
  | { type: 'bookmark'; atMs: number; bookmark: SessionBookmark }

/** Segments and bookmarks in time order; a bookmark sorts before a segment at the same time. */
function buildTimeline(
  segments: readonly TranscriptSegment[],
  bookmarks: readonly SessionBookmark[]
): TimelineItem[] {
  const items: TimelineItem[] = [
    ...segments.map(segment => ({ type: 'segment' as const, atMs: segment.startMs, segment })),
    ...bookmarks.map(bookmark => ({ type: 'bookmark' as const, atMs: bookmark.atMs, bookmark })),
  ]
  const rank = (item: TimelineItem) => (item.type === 'bookmark' ? 0 : 1)
  return items.sort((a, b) => a.atMs - b.atMs || rank(a) - rank(b))
}

export interface EntityGroup {
  kind: TranscriptEntityKind
  entities: readonly TranscriptEntity[]
}

/** Everything the transcript view derives from its data; compute it once per transcript. */
export interface TranscriptOverview {
  timeline: readonly TimelineItem[]
  speakerCount: number
  /** Distinct languages, in order of first use. */
  languages: readonly string[]
  /** Non-empty entity groups in `TRANSCRIPT_ENTITY_KINDS` order; `null` = no LLM. */
  entityGroups: readonly EntityGroup[] | null
}

export function transcriptOverview(transcript: TranscriptData): TranscriptOverview {
  const { segments, bookmarks, entities } = transcript
  return {
    timeline: buildTimeline(segments, bookmarks),
    speakerCount: new Set(segments.map(s => s.speaker)).size,
    languages: [...new Set(segments.map(s => s.language))],
    entityGroups:
      entities &&
      TRANSCRIPT_ENTITY_KINDS.map(kind => ({
        kind,
        entities: entities.filter(entity => entity.kind === kind),
      })).filter(group => group.entities.length > 0),
  }
}
